// External Imports
import HCaptcha from '@hcaptcha/react-hcaptcha';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Internal Imports
import { CAPTCHA_ERRORS, MAX_RETRY_COUNT } from 'src/dataLayer/localData/captcha.js';
import { reportCaptchaError } from 'src/dataLayer/remoteData/rollbar/index.js';
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext.js';

// Component Definition
function CaptchaFeature({ captchaConfig, setCaptchaToken, shouldExecuteCaptcha }) {
  const isUatTest = !!window.Cypress;
  if(isUatTest){
    setCaptchaToken('dummy-e2e-test-token');
    return null;
  }
  if (!captchaConfig) {
    return null;
  }
  const { params: { site_key: siteKey}} = captchaConfig;
  const { locale } = useRouteParamsContext();
  const captchaRef = useRef(null);
  const [retryCount, setRetryCount] = useState(0);

  const executeCaptcha = useCallback(() => {
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
      captchaRef.current.execute('captcha-login');
    }
  }, [captchaRef.current]);

  useEffect(() => {
    if (shouldExecuteCaptcha) {
      executeCaptcha();
    }
  }, [executeCaptcha, shouldExecuteCaptcha]);

  const onVerifyCaptcha = (token) => {
    if (token) {
      setCaptchaToken(token);
    }
  };

  const onErrorCaptcha = (error) => {
    if (CAPTCHA_ERRORS.has(error) && retryCount < MAX_RETRY_COUNT) {
      setRetryCount((previousRetryCount) => previousRetryCount + 1);
      executeCaptcha();
      reportCaptchaError(
        `Error while verifying captcha. Number of retry attempts: ${retryCount + 1}`,
        new Error(error),
      );
    } else {
      reportCaptchaError('Error while retry verifying captcha:', new Error(error));
    }
  };

  const onExpireCaptcha = () => {
    setCaptchaToken('');
    executeCaptcha();
  };

  return (
    <HCaptcha
      id="captcha-login"
      ref={captchaRef}
      sitekey={siteKey}
      onVerify={onVerifyCaptcha}
      onError={onErrorCaptcha}
      onExpire={onExpireCaptcha}
      onChalExpired={onExpireCaptcha}
      size="invisible"
      languageOverride={locale === 'en_GB' ? 'en' : locale}
    />
  );
}

export { CaptchaFeature };
